import React, { useRef, useState, useCallback } from 'react';
import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

/* ── Align icons ── */
const AlignIcon = ({ type }) => {
  const defs = {
    left:   [[0,1,10,1],[0,4,7,4],[0,7,10,7]],
    center: [[1,1,9,1],[2.5,4,7.5,4],[1,7,9,7]],
    right:  [[0,1,10,1],[3,4,10,4],[0,7,10,7]],
  };
  return (
    <svg width="14" height="9" viewBox="0 0 10 9" fill="none">
      {defs[type].map(([x1,y1,x2,y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      ))}
    </svg>
  );
};

/* ── NodeView ── */
const ResizableImageView = ({ node, updateAttributes, selected }) => {
  const imgRef    = useRef(null);
  const dragData  = useRef({ x: 0, w: 0, dir: 1 });
  const [isResizing, setIsResizing] = useState(false);

  const { src, alt, title, width, align = 'center' } = node.attrs;

  const startResize = useCallback((e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imgRef.current) return;
    dragData.current = {
      x:   e.clientX,
      w:   imgRef.current.getBoundingClientRect().width,
      dir,
    };
    setIsResizing(true);

    const onMove = (ev) => {
      const dx  = (ev.clientX - dragData.current.x) * dragData.current.dir;
      const nw  = Math.max(80, Math.round(dragData.current.w + dx));
      updateAttributes({ width: nw });
    };
    const onUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',  onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',  onUp);
  }, [updateAttributes]);

  const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
  const marginMap  = {
    left:   '1.5rem 1.5rem 1.5rem 0',
    center: '1.5rem auto',
    right:  '1.5rem 0 1.5rem 1.5rem',
  };

  return (
    <NodeViewWrapper
      style={{ display: 'flex', justifyContent: justifyMap[align], margin: marginMap[align] }}
      contentEditable={false}
    >
      <div
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        contentEditable={false}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          title={title || ''}
          data-align={align}
          draggable={false}
          style={{
            width:       width ? `${width}px` : '100%',
            maxWidth:    '100%',
            height:      'auto',
            display:     'block',
            borderRadius: '10px',
            boxShadow:   selected
              ? '0 0 0 2px #A67C61, 0 4px 20px rgba(0,0,0,0.1)'
              : '0 4px 20px rgba(0,0,0,0.1)',
            cursor:      isResizing ? 'ew-resize' : 'default',
            userSelect:  'none',
          }}
        />

        {selected && (
          <>
            {/* Floating toolbar */}
            <div className="img-toolbar">
              {['left','center','right'].map(a => (
                <button
                  key={a}
                  type="button"
                  title={`Align ${a}`}
                  className={`img-tb-btn${align === a ? ' active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); updateAttributes({ align: a }); }}
                >
                  <AlignIcon type={a} />
                </button>
              ))}
              <span className="img-tb-sep" />
              {width && <span className="img-tb-size">{width}px</span>}
              <button
                type="button"
                title="Reset to full width"
                className="img-tb-btn"
                onMouseDown={e => { e.preventDefault(); updateAttributes({ width: null }); }}
              >
                ↔
              </button>
            </div>

            {/* Corner handles: E side = dir +1, W side = dir -1 */}
            <div className="img-handle img-handle--nw" onMouseDown={e => startResize(e, -1)} />
            <div className="img-handle img-handle--ne" onMouseDown={e => startResize(e,  1)} />
            <div className="img-handle img-handle--sw" onMouseDown={e => startResize(e, -1)} />
            <div className="img-handle img-handle--se" onMouseDown={e => startResize(e,  1)} />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

/* ── TipTap extension (extends built-in Image) ── */
export const ResizableImage = Image.extend({
  group:      'block',
  draggable:  true,
  selectable: true,
  atom:       true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML:  el => { const w = el.getAttribute('width'); return w ? parseInt(w, 10) : null; },
        renderHTML: a  => a.width ? { width: String(a.width) } : {},
      },
      align: {
        default:    'center',
        parseHTML:  el => el.getAttribute('data-align') || 'center',
        renderHTML: a  => ({ 'data-align': a.align || 'center' }),
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
